import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Field, Input, Select } from "@/components/ui/Field";
import { EmptyState, ErrorState, Loading } from "@/components/ui/States";
import { cn } from "@/lib/cn";
import {
  BOOK_AGAIN_LABEL,
  crewStats,
  type BookAgain,
  type CrewStats,
} from "@/lib/crew";
import { formatDateShort, today, type IsoDate } from "@/lib/dates";
import { formatMoney, pluralise } from "@/lib/format";
import { TEAM_ROLE_LABEL, type TeamRole } from "@/lib/jobs";
import { describeError } from "@/lib/supabase";
import {
  useCrewHistory,
  useCrewPayments,
  useCrewSeats,
  useSaveTeamMember,
  useTeamMembers,
  type CrewHistory,
  type CrewPayment,
  type CrewSeat,
  type TeamMember,
} from "./queries";

type Filter = "all" | "photo" | "video";

const BOOK_AGAIN_TONE = { yes: "sage", maybe: "amber", no: "clay" } as const;

/** Each person's record from their seats and payments (lib/crew.ts crewStats). */
export function statsFor(
  member: TeamMember,
  seats: CrewSeat[],
  payments: CrewPayment[],
  history: CrewHistory[] = [],
): CrewStats {
  const mine = seats.filter((seat) => seat.member_id === member.id && seat.job);
  const paidBySeat = new Map<string, number>();
  let unlinked = 0;
  for (const payment of payments) {
    if (payment.member_id !== member.id) continue;
    if (payment.job_team_id)
      paidBySeat.set(
        payment.job_team_id,
        (paidBySeat.get(payment.job_team_id) ?? 0) + Number(payment.amount),
      );
    else unlinked += Number(payment.amount);
  }
  return crewStats(
    [
      ...mine.map((seat) => ({
        status: seat.status,
        event_date: seat.job!.event_date as IsoDate,
        fee: seat.fee === null ? null : Number(seat.fee),
        travel_fee: seat.travel_fee === null ? null : Number(seat.travel_fee),
        rating: seat.rating,
        paid: paidBySeat.get(seat.id) ?? 0,
      })),
      // Weddings from before the Studio: shot and already settled.
      ...history
        .filter((row) => row.member_id === member.id)
        .map((row) => ({
          status: "signed" as const,
          event_date: row.event_date as IsoDate,
          fee: null,
          travel_fee: null,
          rating: row.rating,
          paid: 0,
        })),
    ],
    unlinked,
    today(),
  );
}

const STAR_PATH =
  "M7 1.2l1.76 3.57 3.94.57-2.85 2.78.67 3.92L7 10.19l-3.52 1.85.67-3.92L1.3 5.34l3.94-.57L7 1.2z";

/** One star, filled from the left by `fill` (0 to 1); the rest is an outline. */
export function Star({ fill, size = 14 }: { fill: number; size?: number }) {
  return (
    <span className="relative inline-block shrink-0" style={{ width: size, height: size }}>
      <svg viewBox="0 0 14 14" width={size} height={size} className="absolute inset-0 text-taupe" aria-hidden>
        <path d={STAR_PATH} fill="none" stroke="currentColor" strokeWidth="1" strokeLinejoin="round" />
      </svg>
      <span className="absolute inset-y-0 left-0 overflow-hidden" style={{ width: `${Math.max(0, Math.min(1, fill)) * 100}%` }}>
        <svg viewBox="0 0 14 14" width={size} height={size} className="text-amber" aria-hidden>
          <path d={STAR_PATH} fill="currentColor" stroke="currentColor" strokeWidth="1" strokeLinejoin="round" />
        </svg>
      </span>
    </span>
  );
}

/** A rating, filled to the exact value: 4.5 shows four and a half stars. */
export function Stars({ value }: { value: number | null }) {
  if (value === null) return <span className="text-taupe">—</span>;
  return (
    <span title={`${value} out of 5`} className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <span className="inline-flex gap-px">
        {[0, 1, 2, 3, 4].map((index) => (
          <Star key={index} fill={value - index} />
        ))}
      </span>
      <span className="text-taupe">{value.toFixed(1)}</span>
    </span>
  );
}

/**
 * The crew database: everyone who has associate-shot or second-shot for
 * Jena, with what they do, what they charge, how many weddings they've shot,
 * how she rated them and whether she'd book them again. Open a person for
 * their weddings, agreements, invoices and payments. (The "To pay" column came
 * off on 29 Oct 2026 at Jena's ask.)
 */
export function TeamPage() {
  const members = useTeamMembers(true);
  const seats = useCrewSeats();
  const payments = useCrewPayments();
  const history = useCrewHistory();
  const save = useSaveTeamMember();
  const [filter, setFilter] = useState<Filter>("all");
  const [showHidden, setShowHidden] = useState(false);
  const [name, setName] = useState("");
  const [role, setRole] = useState<TeamRole>("second_photographer");

  const rows = useMemo(() => {
    const list = (members.data ?? []).filter(
      (member) =>
        (showHidden || member.active) &&
        (filter === "all" ||
          (filter === "photo" ? member.does_photo : member.does_video)),
    );
    return list.map((member) => ({
      member,
      stats: statsFor(
        member,
        seats.data ?? [],
        payments.data ?? [],
        history.data ?? [],
      ),
    }));
  }, [members.data, seats.data, payments.data, history.data, filter, showHidden]);

  return (
    <div className="pt-5">
      <p className="mb-4 flex flex-wrap gap-x-4 text-[length:var(--text-sm)] text-taupe">
        {members.data ? (
          <span>{pluralise(rows.length, "person", "people")} in your crew database</span>
        ) : (
          "Your crew database."
        )}
      </p>

      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-b-[var(--border-hairline)] pb-3">
        <div className="flex gap-1">
          {(["all", "photo", "video"] as const).map((key) => (
            <Button
              key={key}
              size="sm"
              variant={filter === key ? "primary" : "ghost"}
              onClick={() => setFilter(key)}
            >
              {key === "all" ? "Everyone" : key === "photo" ? "Photo" : "Video"}
            </Button>
          ))}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowHidden(!showHidden)}
        >
          {showHidden ? "Hide people you've hidden" : "Show hidden people"}
        </Button>
      </div>

      {members.isPending ? (
        <Loading label="Loading the team" />
      ) : members.isError ? (
        <div className="pt-6">
          <ErrorState
            title="The team could not load"
            detail={describeError(members.error)}
            onRetry={() => void members.refetch()}
          />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          className="mt-6"
          title="Nobody here yet"
          body="Add each photographer and videographer who shoots with you, once. Their weddings, invoices and payments collect here."
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="ledger mt-1 min-w-[60rem]">
            <thead>
              <tr>
                <th className="label">Name</th>
                <th className="label">Based in</th>
                <th className="label">Does</th>
                <th className="label num">Lead rate</th>
                <th className="label num">Second rate</th>
                <th className="label num">Weddings</th>
                <th className="label">Last</th>
                <th className="label">Rating</th>
                <th className="label">Book again</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ member, stats }) => (
                <tr
                  key={member.id}
                  className={cn(!member.active && "opacity-50")}
                >
                  <td>
                    <Link
                      to={`/team/${member.id}`}
                      className="underline decoration-transparent underline-offset-4 hover:decoration-[var(--color-rule-strong)]"
                    >
                      {member.legal_name ?? member.name}
                    </Link>
                    {member.business_name ? (
                      <span className="block text-[length:var(--text-2xs)] text-taupe">
                        {member.business_name}
                      </span>
                    ) : null}
                  </td>
                  <td className="text-taupe">{member.based_in ?? "—"}</td>
                  <td className="text-taupe">
                    {[
                      member.does_photo ? "Photo" : null,
                      member.does_video ? "Video" : null,
                    ]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </td>
                  <td className="num" data-numeric>
                    {member.lead_rate === null
                      ? "—"
                      : formatMoney(Number(member.lead_rate))}
                  </td>
                  <td className="num" data-numeric>
                    {member.second_rate === null
                      ? "—"
                      : formatMoney(Number(member.second_rate))}
                  </td>
                  <td className="num">
                    {stats.weddings}
                    {stats.upcoming ? (
                      <span className="text-taupe"> +{stats.upcoming}</span>
                    ) : null}
                  </td>
                  <td className="whitespace-nowrap text-taupe">
                    {stats.lastWedding
                      ? formatDateShort(stats.lastWedding)
                      : "—"}
                  </td>
                  <td>
                    <Stars value={stats.averageRating} />
                  </td>
                  <td>
                    {member.book_again ? (
                      <Badge
                        tone={BOOK_AGAIN_TONE[member.book_again as BookAgain]}
                      >
                        {BOOK_AGAIN_LABEL[member.book_again as BookAgain]}
                      </Badge>
                    ) : (
                      <span className="text-taupe">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <form
        className="mt-6 flex flex-wrap items-end gap-3 border-t border-t-[var(--border-hairline)] pt-5"
        onSubmit={(event) => {
          event.preventDefault();
          if (!name.trim()) return;
          save.mutate({
            name: name.trim(),
            usual_role: role,
            does_photo: role.includes("photographer"),
            does_video: role.includes("videographer"),
            sort_order: (members.data?.length ?? 0) + 1,
          });
          setName("");
        }}
      >
        <Field label="Add someone" className="min-w-[12rem] flex-1">
          <Input
            placeholder="The name you call them"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <Field label="Usually" className="w-[12rem]">
          <Select
            value={role}
            onChange={(event) => setRole(event.target.value as TeamRole)}
          >
            {(Object.keys(TEAM_ROLE_LABEL) as TeamRole[]).map((key) => (
              <option key={key} value={key}>
                {TEAM_ROLE_LABEL[key]}
              </option>
            ))}
          </Select>
        </Field>
        <Button type="submit" disabled={!name.trim()} loading={save.isPending}>
          Add to the team
        </Button>
      </form>
    </div>
  );
}
